import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';
import { CustomDatePicker } from '../../../components/FormCom/CustomDatePicker';
import {
  CustomInlineRectangularRadioFieldGroup,
  CustomRectangularRadio,
} from '../../../components/FormCom/CustomInlineRectangularRadioField';
import CustomSelectField from '../../../components/FormCom/CustomSelectField';
import { FormComponent } from '../../../components/FormCom/FormComponent';
import { TextInputField } from '../../../components/FormCom/FormField';
import ImageUploadField from '../../../components/FormCom/ImageUploadField';
import NaijaPhoneInputField from '../../../components/FormCom/NaijaPhoneInputField';
import NumberOnlyInputField from '../../../components/FormCom/NumberOnlyInputField';
import ContainedPlainSuccessModal from '../../../components/Modal/ContainedPlainSuccessModal';
import { Modal } from '../../../components/Modal/Modal';
import Tooltip from '../../../components/Tooltip/Tooltip';
import useSetupDriverAccount from '../../../hooks/dashboard/driver-setup/useSetupDriverAccount';
import {
  SetUpADriverAccountInitialValues,
  SetUpADriverAccountSchema,
} from '../../../models/dashboard/driver-setup/setup-a-drivers-account';
import { useAppSelector } from '../../../redux/store';
import { DRIVER_LIST } from '../../../routes/routeConstants';

const SingleDriverSetupForm = () => {
  const [showModal, setShowModal] = useState(false);
  const [successDescription, setSuccessDescription] = useState<string>('');
  const navigate = useNavigate();
  const [defaultImgUrl, setDefaultImgUrl] = useState<string | null>(null);
  const { setupDriverAccount, loading } = useSetupDriverAccount();
  const { vehicleCategoryData } = useAppSelector(
    (state) => state.vehicleCategoryData,
  );

  const fleetCategories = useMemo<Record<string, string>>(
    () =>
      (vehicleCategoryData ?? []).reduce<Record<string, string>>(
        (acc, category) => {
          acc[category.displayName] = category.name;
          return acc;
        },
        {},
      ),
    [vehicleCategoryData],
  );

  const handleOnSubmit = (values: any, actions: any) => {
    setupDriverAccount(
      { ...values, fleetCategory: fleetCategories[values.fleetCategory] },
      () => {
        actions.resetForm(SetUpADriverAccountInitialValues);
        setSuccessDescription(
          `Driver account created for ${values.firstName} ${values.lastName}`,
        );

        setShowModal(true);
      },
    );
  };

  const successActionFn = () => {
    setShowModal(false);
    navigate(`/${DRIVER_LIST}`);
  };

  return (
    <Container>
      <FormComponent
        initialValues={SetUpADriverAccountInitialValues}
        schema={SetUpADriverAccountSchema}
        onSubmit={handleOnSubmit}
      >
        {/* row 1 */}
        <ImageUploadField
          id='profilePicture'
          name='profilePicture'
          defaultImgUrl={defaultImgUrl}
          setDefaultImgUrl={setDefaultImgUrl}
        />

        {/* row 2 */}
        <FormRow>
          <TextInputField
            label='First name'
            name='firstName'
            id='firstName'
            type='text'
            placeholder='First name'
          />
          <TextInputField
            label='Last name'
            name='lastName'
            id='lastName'
            type='text'
            placeholder='Last name'
          />
        </FormRow>

        {/* row 3 */}
        <FormRow>
          <TextInputField
            label='Email address (Optional)'
            name='email'
            id='email'
            type='email'
            placeholder='Email address'
          />
          <NaijaPhoneInputField
            label='Phone number'
            name='phoneNumber'
            id='phoneNumber'
            type='text'
            placeholder='Phone number'
          />
        </FormRow>

        {/* row 4 */}
        <FormRow>
          <CustomDatePicker
            label='Start date'
            name='startDate'
            id='startDate'
            placeholder='Select date'
          />
          <CustomInlineRectangularRadioFieldGroup name='gender' label='Gender'>
            <CustomRectangularRadio name='gender' value='Male'>
              Male
            </CustomRectangularRadio>
            <CustomRectangularRadio name='gender' value='Female'>
              Female
            </CustomRectangularRadio>
          </CustomInlineRectangularRadioFieldGroup>
        </FormRow>

        {/* row 5 */}
        <FormRow>
          <CustomSelectField
            label='Category of fleet'
            name='fleetCategory'
            id='fleetCategory'
            placeholder='Select category of fleet...'
            optionData={[...Object.keys(fleetCategories)]}
          />

          <CustomDatePicker
            label='Date of Birth'
            name='dateOfBirth'
            id='dateOfBirth'
            placeholder='Select date'
          />
        </FormRow>

        {/* row 6 */}
        <FormRow>
          <NumberOnlyInputField
            label='NIN number'
            name='nin'
            id='nin'
            type='text'
            maxLength={11}
            placeholder='NIN number'
          />

          {/* <GooglePlacesProvider>
            <GooglePlacesInputField
              label='House address'
              name='address'
              id='address'
              type='text'
              placeholder='House address'
            />
          </GooglePlacesProvider> */}

          <TextInputField
            label='House address'
            name='address'
            id='address'
            type='text'
            placeholder='House address'
          />
        </FormRow>

        {/* row 7 */}
        <FormRow>
          <CustomSelectField
            label={
              <span>
                Employment type{' '}
                <Tooltip content='Gig workers are independent contractors who may provide services to multiple organizations. Exclusive staff are dedicated to your organization and are not expected to work for other employers.' />
              </span>
            }
            name='employmentType'
            id='employmentType'
            placeholder='Select employment type...'
            optionData={['Gig', ' Exclusive']}
          />
        </FormRow>

        {/* row 8 */}
        <FormRow>
          <FormSubmitButton
            loading={loading}
            className='fit flex-end full-mobile'
          >
            Create Driver Profile
          </FormSubmitButton>
        </FormRow>
      </FormComponent>

      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ContainedPlainSuccessModal
          successActionFn={successActionFn}
          description={successDescription}
          btnLabel='View Driver List'
          hasCancelBtn={true}
        />
      </Modal>
    </Container>
  );
};

export default SingleDriverSetupForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  max-width: 886px;
  border-radius: 8px;
  padding: 24px;

  @media (max-width: 884px) {
    background-color: white;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
  }
`;
